import React, { useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import PageBar from '../../components/PageBar';
import { TEMPLATES, buildTemplate, templateFileName } from '../../lib/templates';
import { planRows } from '../../lib/importPlan';
import ImportPreview from '../../components/ImportPreview';
import { useBranding } from '../../components/BrandingContext';
import { useOrg } from '../../components/OrgContext';
import { useAuth } from '../../components/AuthContext';
import { OnHandIcon } from '../../components/brand/OnHandLogo';
import { uploadSiteLogo, clearSiteLogo } from '../../lib/siteLogo';
import { logAudit } from '../../hooks/useAudit';

export default function SiteUploadsPage({ toast }) {
  const { companyLogoUrl, companyName, refreshCompanyLogo } = useBranding();
  const { current } = useOrg();
  const { profile } = useAuth();
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoRemoving, setLogoRemoving] = useState(false);
  const logoInputRef = useRef(null);

  async function handleLogoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      await uploadSiteLogo(file, current?.id);
      await refreshCompanyLogo();
      toast.success('Logo uploaded.');
    } catch (err) {
      toast.error('Upload failed: ' + (err.message || 'Unknown error'));
    }
    setLogoUploading(false);
    if (logoInputRef.current) logoInputRef.current.value = '';
  }

  async function handleLogoRemove() {
    setLogoRemoving(true);
    try {
      await clearSiteLogo();
      await refreshCompanyLogo();
      toast.success('Logo removed.');
    } catch (err) {
      toast.error('Failed to remove logo.');
    }
    setLogoRemoving(false);
  }

  // ---------- DATA UPLOAD ROW DEFINITIONS ----------
  /*
    **Bringing data in, rebuilt 6 September 2026 to Group D's approved drawing**
    and to job 14: a name, a line saying what the file is, a Template button and
    Choose a file. There is no Upload button, because choosing the file is the
    gesture and **what happens next is a question, not an action**: OnHand reads
    the file, works out what it would do, and asks.

    The ten kinds of file are defined once in `src/lib/templates.js`. This
    screen knows nothing about any of them beyond what is on that list, which is
    the point: a template and the code that reads it back cannot drift apart if
    there is only one of each.
  */
  const [pending, setPending] = useState(null);   // { def, plan, file, ctx }
  const [applying, setApplying] = useState(false);
  const [reading, setReading] = useState(null);   // which one is being read
  const inputs = useRef({});

  async function chooseFile(def, file) {
    if (!file) return;
    setReading(def.key);
    try {
      const [rows, existing] = await Promise.all([def.parse(file), def.existing()]);
      /*
        **A row whose key is not already here is only "new" if this kind of file
        can create one.** People and barcodes cannot: a person's row is keyed to
        their login, and a barcode belongs to an item that has to exist first. So
        for those two an unknown key is a row that cannot be read, with the
        reason on it, rather than a row silently doing nothing.
      */
      const read = def.unknownIsError
        ? (row, i) => {
            const out = def.read(row, i);
            if (!out || out.error) return out;
            if (!existing.has(out.key)) return { error: out.key + ': ' + def.unknownIsError };
            return out;
          }
        : def.read;
      const plan = planRows({ rows, read, existing });
      setPending({ def, plan, file, existing });
    } catch (e) {
      toast.error('Could not read that file: ' + e.message);
    }
    setReading(null);
    if (inputs.current[def.key]) inputs.current[def.key].value = '';
  }

  async function applyPending() {
    const { def, plan, existing } = pending;
    setApplying(true);
    let out;
    try {
      out = await def.apply(plan, { people: await peopleById(existing), orgId: current?.id });
    } catch (e) {
      toast.error('Could not load that file: ' + e.message);
      setApplying(false);
      return;
    }
    setApplying(false);
    setPending(null);

    const wrote = out.written || 0;
    const failed = out.failed || [];
    /*
      **The message says what actually landed**, in the company it landed in.
      Every one of these used to add the size of a batch to a running total
      whether the write succeeded or not, and then call the lot "imported".
    */
    if (wrote && !failed.length) {
      toast.success('Loaded ' + wrote.toLocaleString() + ' into ' + (current?.name || 'this company') + '.' + (out.note || ''));
    } else if (wrote) {
      toast.warning('Loaded ' + wrote.toLocaleString() + '. ' + failed.length + ' could not be saved: ' + failed[0].why);
    } else {
      toast.error('Nothing was saved. ' + (failed[0]?.why || 'The database refused every row.'));
    }
    await logAudit(profile, AUDIT_ACTION[def.key] || 'IMPORT_ACCOUNTS', 'import', {
      target_name: def.label,
      details: { written: wrote, new: plan.create.length, changed: plan.update.length, failed: failed.length },
    });
  }

  /*
    The people import needs each person's id, and the list of who is here
    already carries it. It is read again here rather than kept on the plan,
    because a plan is only ever a description of what would happen.
  */
  async function peopleById(existing) {
    if (!existing) return new Map();
    const { data } = await supabase.rpc('site_users');
    return new Map((data || []).filter(p => p.email).map(p => [p.email.trim().toLowerCase(), p]));
  }

  async function downloadTemplate(def, withRows) {
    setReading(def.key);
    let rows = null;
    if (withRows) {
      const existing = await def.existing();
      rows = [...existing.values()];
    }
    const csv = buildTemplate(def, rows);
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = templateFileName(def, withRows);
    a.click();
    URL.revokeObjectURL(a.href);
    setReading(null);
    if (withRows) toast.success('Downloaded ' + (rows.length).toLocaleString() + ' rows to edit and send back.');
  }

  const AUDIT_ACTION = {
    warehouses: 'WAREHOUSES_IMPORTED',
    users: 'IMPORT_USERS',
    accounts: 'IMPORT_ACCOUNTS',
    referrals: 'IMPORT_REFERRAL_SOURCES',
    items_claimsoft: 'IMPORT_CLAIMSOFT_CATALOG',
    items_edge: 'IMPORT_EDGE_CATALOG',
    barcodes: 'IMPORT_BARCODES',
    vendor_aliases: 'IMPORT_VENDOR_ALIASES',
  };

  return (
    <div>
      {/* **One screen, one name.** The menu tab said "Branding & Uploads" and
          this bar said "Site Uploads", on the same screen. The menu wins, and
          the name is settled in docs/design/VOCABULARY.md. */}
      <PageBar title="Branding & Uploads" subtitle="The logo, and bringing data in" />

      {pending && (
        <ImportPreview
          def={pending.def}
          plan={pending.plan}
          company={current?.name}
          fileName={pending.file.name}
          busy={applying}
          onClose={() => setPending(null)}
          onConfirm={applyPending}
        />
      )}

      <div className="card imp-card">
        <div className="imp-card-head">
          <div className="imp-title">Bringing data in</div>
          <div className="imp-sub">
            {TEMPLATES.length} kinds of file. Each one takes a CSV that matches its template,
            and says what it would do before it does it.
            {/* The order is not decoration. A location can only be paired with a
                referral source that already exists, a location can only sit in a
                region that already exists, and neither catalog can be matched
                without the vendor list. Loading a company from nothing goes down
                this list. */}
            <b> Loading a company from nothing goes down this list in order.</b>
          </div>
        </div>
        {TEMPLATES.map(def => (
          <div className="imp-row" key={def.key}>
            <div className="imp-row-main">
              <div className="imp-row-name">{def.label}</div>
              <div className="imp-row-says">{def.says}</div>
            </div>
            <div className="imp-row-acts">
              <button className="tb-btn tb-btn-ghost" disabled={reading === def.key}
                onClick={() => downloadTemplate(def, false)}>Template</button>
              {/* The half of a template that makes it worth having: what is
                  already here, in the same columns, ready to be changed and sent
                  back. */}
              <button className="tb-btn tb-btn-ghost" disabled={reading === def.key}
                onClick={() => downloadTemplate(def, true)}>With current rows</button>
              <button className="tb-btn" disabled={reading === def.key}
                onClick={() => inputs.current[def.key]?.click()}>
                {reading === def.key ? 'Reading...' : 'Choose a file'}
              </button>
            </div>
            <input type="file" accept=".csv,.txt" style={{ display: 'none' }}
              ref={el => { inputs.current[def.key] = el; }}
              onChange={e => chooseFile(def, e.target.files?.[0])} />
          </div>
        ))}
      </div>

      {/* ---------- BRANDING ---------- */}
      {/* Two rows, and the order is the message: the OnHand mark is shown first
          and has no controls, so it reads as a fixed part of the product rather
          than something an admin forgot they could change. The company's own
          logo is the row below, and is the only one with buttons. */}
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 10 }}>
        Branding
      </div>

      <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border)', borderRadius: 'var(--oh-radius-control)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 20, boxShadow: 'var(--shadow-sm)', marginBottom: 10 }}>
        <div style={{
          width: 72, height: 72,
          background: 'var(--hover)',
          border: '1px solid var(--border-soft)',
          borderRadius: 'var(--oh-radius-chip)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', flexShrink: 0,
        }}>
          <OnHandIcon size={36} decorative />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>OnHand</div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>The product mark. Shown on every screen and cannot be changed.</div>
        </div>
      </div>

      <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border)', borderRadius: 'var(--oh-radius-control)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 20, boxShadow: 'var(--shadow-sm)' }}>
        <div style={{
          width: 72, height: 72,
          background: 'var(--hover)',
          border: '1px solid var(--border-soft)',
          borderRadius: 'var(--oh-radius-chip)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', flexShrink: 0,
        }}>
          {companyLogoUrl
            ? <img src={companyLogoUrl} alt="Current company logo" style={{ maxHeight: 56, maxWidth: 64, objectFit: 'contain' }} />
            : <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>No logo</div>}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>
            {companyName ? companyName + ' logo' : 'Company logo'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
            Sits beside the OnHand mark in the header - PNG, JPG, SVG or WebP, up to 2 MB
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}>
            Applies to this site only. Other companies keep their own.
          </div>
        </div>
        <input
          type="file"
          accept="image/png,image/jpeg,image/svg+xml,image/webp"
          ref={logoInputRef}
          style={{ display: 'none' }}
          onChange={handleLogoUpload}
        />
        <button className="ul-btn"
          onClick={() => logoInputRef.current?.click()}
          disabled={logoUploading}
          style={{ fontSize: 12, fontWeight: 600, padding: '7px 12px', borderRadius: 'var(--oh-radius-chip)', background: 'var(--surface-glass)', border: '1px solid var(--border)', color: 'var(--text-secondary)', cursor: logoUploading ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
        >
          {logoUploading ? 'Uploading...' : (companyLogoUrl ? 'Replace' : 'Upload')}
        </button>
        {companyLogoUrl && (
          <button className="ul-btn"
            onClick={handleLogoRemove}
            disabled={logoRemoving}
            style={{ fontSize: 12, fontWeight: 600, padding: '7px 12px', borderRadius: 'var(--oh-radius-chip)', background: 'var(--critical-bg)', border: '1px solid var(--critical-bg)', color: 'var(--critical-ink)', cursor: logoRemoving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
          >
            {logoRemoving ? 'Removing...' : 'Remove'}
          </button>
        )}
      </div>
    </div>
  );
}
